import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { CacheObservable, compareEntities } from '@lycoris-nubila/ngx-lycoris';
import {
	filter,
	isEmpty,
	isEqual,
	isNil,
	omit,
	reject,
	uniqBy,
} from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

import { IamUserTaskExportTaskOutput } from 'user/model/iam-user-task-export-task.output';
import { UserTaskExportTaskStatus } from 'user/model/user-task-export-task-status';
import { ObserveUserTaskExportTasksByCriteriaQuery } from 'user/query/task-export-task/observe-user-task- export-tasks-by-criteria.query';
import { UserTaskExportTask } from 'user/taskExportTask/user-task-export-task';
import {
	UserTaskExportTaskDocument,
	UserTaskExportTaskState,
	UserTaskExportTaskStore,
} from 'user/taskExportTask/user-task-export-task.store';

@Injectable({ providedIn: 'root' })
export class UserTaskExportTaskRepository extends QueryEntity<UserTaskExportTaskState> {
	constructor(protected store: UserTaskExportTaskStore) {
		super(store);
	}

	@CacheObservable()
	public observeUserTaskExportTasksByCriteria(
		query: ObserveUserTaskExportTasksByCriteriaQuery
	): Observable<UserTaskExportTask[]> {
		return this.selectAll({
			filterBy: [
				(doc) => !query.status || query.status === doc.status,
				(doc) =>
					!query.exportedTasksType ||
					query.exportedTasksType === doc.exportedTasksType,
				(doc) => !query.userId || query.userId === doc.userId,
				(doc) =>
					!query.exportedTasksParticipantId ||
					query.exportedTasksParticipantId === doc.exportedTasksParticipantId,
				(doc) =>
					!doc.dueTimestamp ||
					!query.dueStartTimestamp ||
					!query.dueEndTimestamp ||
					(doc.dueTimestamp >= query.dueStartTimestamp &&
						doc.dueTimestamp <= query.dueEndTimestamp),
				(doc) =>
					!query.exportedTaskStatuses ||
					isEmpty(query.exportedTaskStatuses) ||
					!doc.exportedTaskStatuses ||
					isEmpty(doc.exportedTaskStatuses) ||
					query.exportedTaskStatuses.some(
						(s) => doc.exportedTaskStatuses && doc.exportedTaskStatuses.includes(s)
					),
				(doc) =>
					!query.exportedTasksParticipantName ||
					!doc.exportedTasksParticipantName ||
					doc.exportedTasksParticipantName
						.toLowerCase()
						.includes(query.exportedTasksParticipantName.toLowerCase()),
			],
			sortByOrder: query.direction || Order.DESC,
			sortBy: 'creationTimestamp',
		}).pipe(
			distinctUntilChanged(compareEntities),
			map((documents) =>
				documents.map((document: UserTaskExportTaskDocument) =>
					this.store.asEntity(document)
				)
			)
		);
	}

	@CacheObservable()
	public observeUserTaskExportTaskById(
		id: string
	): Observable<UserTaskExportTask> {
		return this.selectEntity(id).pipe(
			distinctUntilChanged(isEqual),
			map((document: UserTaskExportTaskDocument) => this.store.asEntity(document))
		);
	}

	@CacheObservable()
	public observeUserTaskExportTasksByIds(
		ids: string[]
	): Observable<UserTaskExportTask[]> {
		return this.selectMany(ids).pipe(
			distinctUntilChanged<UserTaskExportTaskDocument[]>(isEqual),
			map((documents) =>
				documents.map((document: UserTaskExportTaskDocument) =>
					this.store.asEntity(document)
				)
			)
		);
	}

	public deleteTaskUserExportTask(id: string): void {
		this.store.remove(id);
	}

	public saveUserTaskExportTasks(entities: IamUserTaskExportTaskOutput[]): void {
		this.store.upsertMany(
			uniqBy(
				reject(entities, isNil).map((e) => this.store.asDocument(e)),
				'id'
			)
		);
	}
}
